import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";

import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";

import useDebounce from "@/hooks/useDebounce";
import { usePageStore } from "@/hooks/usePageStore";
import { useUserStore } from "@/hooks/useUserStore";
import {
  useAuditLogFirstRender,
  useCreateAuditlog,
} from "@/hooks/useAuditLogs";
import {
  useArchiveOrganization,
  useCreateOrganization,
  useOrganizations,
  useUpdateOrganization,
} from "@/hooks/useOrganizations";
import { useActiveOrganizationLabels } from "@/hooks/useOrganizationLabels";

import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useAppTheme } from "@/utils/theme";
import { toTitleCase } from "@/utils/toTitleCase";

import {
  Organization,
  OrganizationPayload,
} from "@/domain/organizations.interface";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";

import OrganizationFilters, {
  Filters,
} from "@/ui/content/organizations/Filters";
import OrganizationModal from "@/ui/modals/organizations/OrganizationModal";

const DEFAULT_FILTERS: Filters = {
  sortField: "name",
  sortOrder: 1,
  pageNumber: 0,
  pageSize: 10,
  search: "",
  status: "",
};

const OrganizationsPage: FC = () => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const { setPageTitle } = usePageStore();

  const filtersState = useState<Filters>(DEFAULT_FILTERS);
  const [filters, setFilters] = filtersState;
  const debouncedSearch = useDebounce({ value: filters.search });

  const [modalState, setModalState] = useState<{
    open: boolean;
    organization?: Organization;
  }>({ open: false });

  const [deleteAlert, setDeleteAlert] = useState<{
    open: boolean;
    organization?: Organization;
  }>({ open: false });

  const {
    organizations,
    isLoadingOrganizations,
    isFetchingOrganizations,
    refetchOrganizations,
  } = useOrganizations({
    sortField: filters.sortField,
    sortOrder: filters.sortOrder,
    pageNumber: filters.pageNumber,
    pageSize: filters.pageSize,
    search: debouncedSearch,
    status: filters.status,
  });

  const createOrganization = useCreateOrganization();
  const updateOrganization = useUpdateOrganization();
  const archiveOrganization = useArchiveOrganization();
  const createAuditLog = useCreateAuditlog();

  const { activeOrganizationLabels } = useActiveOrganizationLabels();

  const labelNameById = useMemo(() => {
    const map = new Map<string, string>();
    (activeOrganizationLabels || []).forEach((label) => {
      map.set(label._id, label.name);
    });
    return map;
  }, [activeOrganizationLabels]);

  const isSaving = createOrganization.isPending || updateOrganization.isPending;

  useEffect(() => {
    setPageTitle("Organizations");
  }, []);

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.ORGANIZATIONS,
    option: OPTIONS.ORGANIZATIONS_LIST,
    actionCode: ACTION_CODE.WEB_ORG_LIST_VIEW,
    action: "When entering",
    detail: "Viewed the list of organizations",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    programId: userInfo?.programId as string,
    orgUnitId: userInfo?.orgUnitIds?.[0] as string,
  });

  useEffect(() => {
    refetchOrganizations();
  }, [
    debouncedSearch,
    filters.status,
    filters.sortField,
    filters.sortOrder,
    filters.pageNumber,
    filters.pageSize,
  ]);

  useEffect(() => {
    if (debouncedSearch !== "") {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.ORGANIZATIONS,
        option: OPTIONS.ORGANIZATIONS_LIST,
        actionCode: ACTION_CODE.WEB_ORG_LIST_SEARCH,
        action: "When searching",
        detail: "Searched within the list of organizations",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });
    }
  }, [debouncedSearch]);

  const handleOpenCreate = () => {
    setModalState({ open: true, organization: undefined });
  };

  const handleOpenEdit = (organization: Organization) => {
    setModalState({ open: true, organization });
  };

  const handleCloseModal = () => {
    setModalState({ open: false, organization: undefined });
  };

  const handleSave = async (data: OrganizationPayload) => {
    const editingOrg = modalState.organization;

    try {
      if (editingOrg) {
        await updateOrganization.mutateAsync({
          id: editingOrg._id,
          payload: data,
        });

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ORGANIZATIONS,
          option: OPTIONS.ORGANIZATION_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_ORG_EDIT,
          action: "When editing an organization",
          detail: `Edited organization ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The organization has been updated.", "success");
      } else {
        await createOrganization.mutateAsync(data);

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ORGANIZATIONS,
          option: OPTIONS.ORGANIZATION_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_ORG_ADD,
          action: "When creating an organization",
          detail: `Created organization ${data.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The organization has been created.", "success");
      }

      handleCloseModal();
      refetchOrganizations();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteAlert.organization;
    if (!target) return;

    try {
      await archiveOrganization.mutateAsync(target._id);

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.ORGANIZATIONS,
        option: OPTIONS.DELETE,
        actionCode: ACTION_CODE.WEB_ORG_DELETE,
        action: "When deleting an organization",
        detail: `Deleted organization ${target.name}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });

      showSnackBar("The registry has been deleted.", "success");
      setDeleteAlert({ open: false });
      refetchOrganizations();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: true,
      label: "Name",
      align: "left",
      width: "220px",
    },
    {
      id: "description",
      isSortable: false,
      disablePadding: false,
      label: "Description",
      align: "left",
    },
    {
      id: "labels",
      isSortable: false,
      disablePadding: false,
      label: "Labels",
      align: "left",
      width: "220px",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
      width: "140px",
    },
    {
      id: "createdAt",
      isSortable: true,
      disablePadding: false,
      label: "Created",
      align: "left",
      width: "160px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: "Action",
      align: "right",
      width: "100px",
    },
  ];

  const rows = useMemo(() => {
    if (!organizations || organizations.list.length === 0) return [];

    return organizations.list.map((organization, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEdit(organization),
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => setDeleteAlert({ open: true, organization }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const labelNames = (organization.orgUnitLabelId || [])
        .map((id) => labelNameById.get(id))
        .filter((name): name is string => Boolean(name));

      return createDataRow(index, {
        name: <div>{organization.name}</div>,
        description: <div>{organization.description || "-"}</div>,
        labels: (
          <div>{labelNames.length > 0 ? labelNames.join(", ") : "-"}</div>
        ),
        status: (
          <Chip
            sx={{
              bgcolor:
                organization.status === "ACTIVE"
                  ? theme.palette.brand.light
                  : theme.palette.warning.light,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor:
                organization.status === "ACTIVE"
                  ? theme.palette.brand.main
                  : theme.palette.warning.dark,
            }}
            label={
              <div className="text-xs">{toTitleCase(organization.status)}</div>
            }
          />
        ),
        createdAt: (
          <div>
            {organization.createdAt
              ? dayjs(organization.createdAt).format("DD MMM YYYY")
              : "-"}
          </div>
        ),
        action: <ActionMenu id={organization._id} options={actionMenu} />,
      });
    });
  }, [organizations, theme, labelNameById]);

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="headlineSmall" component="h1">
                    Organizations
                  </Typography>

                  <Button
                    size="medium"
                    onClick={handleOpenCreate}
                    variant="contained"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </Button>
                </Box>

                <OrganizationFilters filtersState={filtersState} />

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order={filters.sortOrder === 1 ? "asc" : "desc"}
                  orderBy={filters.sortField}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber}
                  total={organizations?.totalCount}
                  isLoading={isLoadingOrganizations || isFetchingOrganizations}
                  messageEmptyData="No organizations to show."
                  onChangePage={(_event, newPage) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: newPage,
                    }));
                  }}
                  onChangeSize={(event) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: 0,
                      pageSize: Number(event.target.value),
                    }));
                  }}
                  onChangeSort={(order, orderBy) => {
                    setFilters((prev) => {
                      const newSortField = orderBy as string;
                      const newSortOrder = order === "asc" ? 1 : -1;
                      if (
                        prev.sortField === newSortField &&
                        prev.sortOrder === newSortOrder
                      ) {
                        return prev;
                      }

                      return {
                        ...prev,
                        pageNumber: 0,
                        sortField: newSortField,
                        sortOrder: newSortOrder,
                      };
                    });
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {modalState.open && (
        <OrganizationModal
          open={modalState.open}
          onClose={handleCloseModal}
          organization={modalState.organization}
          onSave={handleSave}
          isPending={isSaving}
        />
      )}

      {deleteAlert.open && (
        <GenericAlertModal
          onCancel={() => setDeleteAlert({ open: false })}
          onClick={handleConfirmDelete}
          title="Delete"
          description="Are you sure you want to delete this registry?"
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveOrganization.isPending}
          theme={theme}
        />
      )}
    </>
  );
};

export default OrganizationsPage;
